/**
 * Functions for managing video carousels and DOM operations
 */

import { getVideoUrl } from './VideoUtils';

/**
 * Add a video to the main carousel
 * @param {Object} video - Video object with source information
 * @param {HTMLElement} mainCarouselWrapper - Main carousel wrapper element
 */
export function addVideoMainSlide(video, mainCarouselWrapper) {
  // Early exit if no main carousel wrapper
  if (!mainCarouselWrapper) {
    console.error('🎬 Could not find main carousel wrapper');
    return;
  }

  // Get the sort order for this video
  let videoSortOrder = 999;
  if (video.id) {
    const idParts = video.id.split('_');
    if (idParts.length >= 2) {
      videoSortOrder = parseInt(idParts[1], 10) || 999;
    }
  } else if (video.sort_order) {
    videoSortOrder = parseInt(video.sort_order, 10);
  } else if (video.sort) {
    videoSortOrder = parseInt(video.sort, 10);
  }

  // Determine video URL using the priority-based approach
  const videoUrl = getVideoUrl(video);
  if (!videoUrl) {
    console.error(`🎬 Could not determine URL for video:`, video);
    return;
  }

  // Get poster image if available
  let posterUrl = '';
  if (video.preview_image) {
    posterUrl = video.preview_image;
  }

  // Create the HTML structure for the main carousel
  const slideHTML = `
  <div class="swiper-slide" data-video-variant-barcode="${video.barcode}" data-product-images-slide data-count="${videoSortOrder}">
      <video playsinline="" id="productVideo" loop muted aria-label="Product video" autoplay poster="${posterUrl}" data-video-variant-barcode="${video.barcode}" data-video-sort-order="${videoSortOrder}" data-product-images-slide>
        <source src="${videoUrl}" type="video/mp4">
        Your browser does not support the video tag.
      </video>
      <button id="playPauseButton" role="button" tabindex="0" aria-pressed="false" aria-label="Pause video">
        <span class="pause" aria-hidden="true">
          <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0.333334V9.66667H2.66667V0.333334H0ZM5.33333 0.333334V9.66667H8V0.333334H5.33333Z" fill="black"></path>
          </svg>
        </span>
        <span class="play" aria-hidden="true">
          <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.666626 0.777802V13.2222L11.3333 7.00002L0.666626 0.777802Z" fill="black"></path>
          </svg>
        </span>
        <span class="visually-hidden">Pause</span>
      </button>
  </div>
  `;

  // Create a temporary div to hold the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = slideHTML.trim();

  // Find the correct position to insert the video based on sort order
  const existingSlides = mainCarouselWrapper.querySelectorAll('.swiper-slide');

  // The sort_order value represents the actual position where the video should appear
  // If sort_order is 3, it should be the third item (index 2 in 0-based indexing)
  const targetPosition = videoSortOrder - 1;

  if (targetPosition >= 0 && targetPosition < existingSlides.length) {
    // Insert at the exact position indicated by sort_order
    mainCarouselWrapper.insertBefore(tempDiv.firstChild, existingSlides[targetPosition]);
  } else {
    // If the position is beyond the current slides, append to the end
    mainCarouselWrapper.appendChild(tempDiv.firstChild);
  }
}

/**
 * Add a video thumbnail to the thumbs carousel
 * @param {Object} video - Video object with source information
 * @param {HTMLElement} thumbsCarouselWrapper - Thumbs carousel wrapper element
 * @param {Object} productData - Product data object
 */
export function addVideoThumbnail(video, thumbsCarouselWrapper, productData) {
  if (!thumbsCarouselWrapper) {
    console.error('🎬 Could not find thumbs carousel wrapper');
    return;
  }

  // Get the sort order for this video
  let videoSortOrder = 999;
  if (video.id) {
    const idParts = video.id.split('_');
    if (idParts.length >= 2) {
      videoSortOrder = parseInt(idParts[1], 10) || 999;
    }
  } else if (video.sort_order) {
    videoSortOrder = parseInt(video.sort_order, 10);
  } else if (video.sort) {
    videoSortOrder = parseInt(video.sort, 10);
  }

  // Get thumbnail preview URL
  let thumbPreviewUrl = getVideoUrl(video, true);

  // If no thumbnail URL was found, try to find a matching preview image in product media
  if (!thumbPreviewUrl && productData && productData.media) {
    const mediaItem = productData.media.find((item) => {
      const isVideoType = item.media_type === 'video' || item.media_type === 'external_video';
      if (!isVideoType) return false;

      // Check for matches in various fields
      const barcodeMatch = item.alt && video.barcode && item.alt.includes(video.barcode);
      const filenameMatch = video.filename && item.src && item.src.includes(video.filename);
      return barcodeMatch || filenameMatch;
    });

    if (mediaItem && mediaItem.preview_image && mediaItem.preview_image.src) {
      thumbPreviewUrl = mediaItem.preview_image.src;
    }
  }

  // Create the HTML structure for the thumbs carousel
  const thumbsSlideHTML = `
  <div class="swiper-slide ProductImages__thumbs-placeholder" data-video-variant-barcode="${video.barcode}" data-count="${videoSortOrder}">
    <div class="ProductImages__thumbs-placeholder-inner">
      <img src="${thumbPreviewUrl}" alt="Video thumbnail for ${video.barcode}" loading="lazy">
      <div class="icon-play">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.9998 0.666687C6.64784 0.666687 0.666504 6.64802 0.666504 14C0.666504 21.352 6.64784 27.3334 13.9998 27.3334C21.3518 27.3334 27.3332 21.352 27.3332 14C27.3332 6.64802 21.3518 0.666687 13.9998 0.666687ZM11.3332 18.6187V9.38135L19.3332 14L11.3332 18.6187Z" fill="white"></path>
        </svg>
      </div>
    </div>
  </div>
  `;

  // Create a temporary div for the thumbnail
  const tempThumbDiv = document.createElement('div');
  tempThumbDiv.innerHTML = thumbsSlideHTML.trim();

  // Apply the same positioning logic for thumbnails
  const existingThumbs = thumbsCarouselWrapper.querySelectorAll('.swiper-slide');
  const targetPosition = videoSortOrder - 1;

  if (targetPosition >= 0 && targetPosition < existingThumbs.length) {
    // Insert at the exact position indicated by sort_order
    thumbsCarouselWrapper.insertBefore(tempThumbDiv.firstChild, existingThumbs[targetPosition]);
  } else {
    // If the position is beyond the current thumbs, append to the end
    thumbsCarouselWrapper.appendChild(tempThumbDiv.firstChild);
  }
}

/**
 * Update video visibility for a specific variant
 * @param {string} variantBarcode - Barcode of the current variant
 * @param {Object} videoData - Video data object
 */
export function updateVideoVisibility(variantBarcode, videoData) {
  // Early exit if no video data is available
  if (!videoData || !videoData.barcodes || videoData.barcodes.length === 0) {
    return;
  }

  // Check if this variant has videos
  const hasVideos = videoData.barcodes.includes(variantBarcode);

  // Get all video slides (both main and thumbnails)
  const mainVideoSlides = document.querySelectorAll('.swiper-main .swiper-slide[data-video-variant-barcode]');
  const thumbVideoSlides = document.querySelectorAll('.swiper-thumbs .swiper-slide[data-video-variant-barcode]');

  // First, hide all video slides
  mainVideoSlides.forEach((slide) => {
    slide.style.display = 'none';
  });

  thumbVideoSlides.forEach((slide) => {
    slide.style.display = 'none';
  });

  // If this variant has videos, show only the matching ones
  if (hasVideos) {
    let visibleMainVideosCount = 0;
    let visibleThumbVideosCount = 0;

    // Show videos that match the current variant in the main carousel
    mainVideoSlides.forEach((slide) => {
      const slideBarcode = slide.getAttribute('data-video-variant-barcode');
      if (slideBarcode === variantBarcode) {
        slide.style.display = '';
        visibleMainVideosCount++;
      }
    });

    // Show videos that match the current variant in the thumbs carousel
    thumbVideoSlides.forEach((slide) => {
      const slideBarcode = slide.getAttribute('data-video-variant-barcode');
      if (slideBarcode === variantBarcode) {
        slide.style.display = '';
        visibleThumbVideosCount++;
      }
    });

    return { visibleMainVideosCount, visibleThumbVideosCount };
  }

  return { visibleMainVideosCount: 0, visibleThumbVideosCount: 0 };
}

/**
 * Sync videos between main carousel and thumbnail carousel
 * @param {string} variantBarcode - Barcode of the current variant
 * @param {HTMLElement} mainSwiperWrapper - Main carousel wrapper element
 * @param {HTMLElement} thumbsSwiperWrapper - Thumbs carousel wrapper element
 * @param {Object} videoData - Video data object
 * @param {Object} productData - Product data object
 */
export function syncVideosBetweenCarousels(
  variantBarcode,
  mainSwiperWrapper,
  thumbsSwiperWrapper,
  videoData,
  productData,
) {
  // Get the variant videos
  const variantVideos = videoData.videos.filter((video) => video.barcode === variantBarcode);
  if (variantVideos.length === 0) return;

  // Get all video slides
  const mainVideoSlides = document.querySelectorAll('.swiper-main .swiper-slide[data-video-variant-barcode]');
  const thumbVideoSlides = document.querySelectorAll('.swiper-thumbs .swiper-slide[data-video-variant-barcode]');

  // Helper function to get sort orders for visible slides
  const getVisibleSortOrders = (slides) => {
    return Array.from(slides)
      .filter(
        (slide) =>
          slide.getAttribute('data-video-variant-barcode') === variantBarcode && slide.style.display !== 'none',
      )
      .map((slide) => slide.getAttribute('data-count'));
  };

  // Get visible sort orders
  const mainSortOrders = getVisibleSortOrders(mainVideoSlides);
  const thumbSortOrders = getVisibleSortOrders(thumbVideoSlides);

  // If we have more main videos than thumbnails, we need to add the missing thumbnails
  if (mainSortOrders.length > thumbSortOrders.length) {
    // Find which videos are missing thumbnails
    const missingSortOrders = mainSortOrders.filter((order) => !thumbSortOrders.includes(order));

    // Add the missing thumbnails
    if (missingSortOrders.length > 0 && thumbsSwiperWrapper) {
      missingSortOrders.forEach((sortOrder) => {
        const matchingVideo = variantVideos.find(
          (v) => parseInt(v.sort_order || 999, 10) === parseInt(sortOrder, 10) || v.id.includes(`_${sortOrder}`),
        );

        if (matchingVideo) {
          addVideoThumbnail(matchingVideo, thumbsSwiperWrapper, productData);
        }
      });
    }
  }
  // If we have more thumbnails than main videos, we need to add the missing main videos
  else if (thumbSortOrders.length > mainSortOrders.length) {
    // Find which videos are missing main slides
    const missingSortOrders = thumbSortOrders.filter((order) => !mainSortOrders.includes(order));

    // Add the missing main videos
    if (missingSortOrders.length > 0 && mainSwiperWrapper) {
      missingSortOrders.forEach((sortOrder) => {
        const matchingVideo = variantVideos.find(
          (v) => parseInt(v.sort_order || 999, 10) === parseInt(sortOrder, 10) || v.id.includes(`_${sortOrder}`),
        );

        if (matchingVideo) {
          addVideoMainSlide(matchingVideo, mainSwiperWrapper);
        }
      });
    }
  }
}
